import { c as _c } from "react/compiler-runtime";
import { patchHostedBusinessVaultMutation } from '@onefootprint/fetch-hosted';
import type { ModernRawBusinessDataRequest } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CollectBusinessData } from '@/idv2/features/collect-business-data';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { type FieldError, useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'business-information'
});
export const Route = createFileRoute('/onboarding/$token/collect-business-data/business-information')({
  component: BusinessInformationPage,
  loader: ({
    context: {
      currentRequirement,
      decryptedVault,
      needsManageBos,
      bootstrapFields,
      stepUpContext
    }
  }) => ({
    currentRequirement,
    decryptedVault,
    needsManageBos,
    bootstrapFields,
    stepUpContext
  })
});
function BusinessInformationPage() {
  const $ = _c(19);
  const {
    currentRequirement,
    decryptedVault,
    needsManageBos,
    bootstrapFields,
    stepUpContext
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const {
    l10n
  } = Route.useRouteContext();
  const footprint = useFootprint();
  const navigate = useNavigate();
  const handleVaultError = useVaultMutationError();
  let t0;
  let t1;
  if ($[0] !== bootstrapFields || $[1] !== token) {
    const headers = {
      "X-Fp-Authorization": token
    };
    if (bootstrapFields.length > 0) {
      headers["X-Fp-Bootstrapped-Fields"] = bootstrapFields.join(",");
    }
    t0 = useMutation;
    t1 = patchHostedBusinessVaultMutation({
      headers
    });
    $[0] = bootstrapFields;
    $[1] = token;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const {
    mutateAsync: vaultMutation,
    isPending
  } = t0(t1);
  let t2;
  if ($[4] !== footprint || $[5] !== handleVaultError || $[6] !== navigate || $[7] !== needsManageBos || $[8] !== token || $[9] !== vaultMutation) {
    t2 = async (data, onError) => {
      logInfo("Business information form submission started", {
        vaultKeys: Object.keys(data).join(",")
      });
      ;
      try {
        await vaultMutation({
          body: data
        });
        logInfo("Business information vault mutation succeeded");
        if (needsManageBos) {
          logInfo("Navigating to business-owners step");
          navigate({
            to: "/onboarding/$token/collect-business-data/business-owners",
            params: {
              token
            }
          });
        } else {
          logInfo("No BOs needed, navigating forward");
          footprint.navigation.forward();
        }
      } catch (t3) {
        const error = t3;
        const fieldErrors = handleVaultError(error, onError);
        const log = fieldErrors.length > 0 ? logWarn : logError;
        log("Business information vault mutation failed", error, {
          fieldErrorCount: fieldErrors.length
        });
      }
    };
    $[4] = footprint;
    $[5] = handleVaultError;
    $[6] = navigate;
    $[7] = needsManageBos;
    $[8] = token;
    $[9] = vaultMutation;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const onSubmit = t2;
  let t3;
  if ($[11] !== currentRequirement || $[12] !== decryptedVault || $[13] !== isPending || $[14] !== l10n || $[15] !== onSubmit || $[16] !== stepUpContext || $[17] !== token) {
    t3 = <CollectBusinessData currentRequirement={currentRequirement} decryptedVault={decryptedVault} isLoading={isPending} onSubmit={onSubmit} l10n={l10n} authToken={token} stepUpContext={stepUpContext} />;
    $[11] = currentRequirement;
    $[12] = decryptedVault;
    $[13] = isPending;
    $[14] = l10n;
    $[15] = onSubmit;
    $[16] = stepUpContext;
    $[17] = token;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
}